import { useState, useEffect, useCallback, useRef } from "react";
// import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Edit2,
  CheckCircle2,
  FileText,
  Plus,
  Trash2,
} from "lucide-react"; // Trash2 qo'shildi
import { adminAPI } from "../utils/adminApi";
import CreateQuestionModal from "../modals/CreateQuestionModal";
import type {
  AdminTicketListItem,
  AdminQuestionResponse,
} from "../utils/adminTypes";
import { QuestionType } from "../utils/adminTypes";
import ImageZoomModal from "../../../components/ImageZoomModal";
import EditQuestionModal from "../modals/EditQuestionModal";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";

type QuestionsMode = "ticket" | "all";
const ALL_QUESTIONS_LIMIT = 20;

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  [QuestionType.ODDIY]: "Oddiy",
  [QuestionType.SONLI]: "Sonli",
  [QuestionType.KALIT_SOZLI]: "Kalit so'zli",
  [QuestionType.MURAKKAB]: "Murakkab",
};

// Turlar bir-biridan uzoqdan ham ajralib turishi uchun har biriga alohida rang
const QUESTION_TYPE_COLORS: Record<QuestionType, { bg: string; border: string; text: string }> = {
  [QuestionType.ODDIY]: {
    bg: "rgba(100,116,139,.12)",
    border: "rgba(100,116,139,.3)",
    text: "#94A3B8",
  },
  [QuestionType.SONLI]: {
    bg: "rgba(59,130,246,.12)",
    border: "rgba(59,130,246,.3)",
    text: "#60A5FA",
  },
  [QuestionType.KALIT_SOZLI]: {
    bg: "rgba(168,85,247,.12)",
    border: "rgba(168,85,247,.3)",
    text: "#C084FC",
  },
  [QuestionType.MURAKKAB]: {
    bg: "rgba(245,158,11,.12)",
    border: "rgba(245,158,11,.3)",
    text: "#F59E0B",
  },
};

interface QuestionsTabProps {
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

export default function QuestionsTab({
  onError,
  onSuccess,
}: QuestionsTabProps) {
  // ─── State ──────────────────────────────────────────────────────
  const [mode, setMode] = useState<QuestionsMode>("ticket");
  const [tickets, setTickets] = useState<AdminTicketListItem[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<AdminQuestionResponse[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [ticketTotal, setTicketTotal] = useState(0);
  const [editQuestionId, setEditQuestionId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const questionsScrollRef = useRef<HTMLDivElement | null>(null);

  // "Barcha savollar" rejimi uchun alohida state
  const [allSearch, setAllSearch] = useState("");
  const [allPage, setAllPage] = useState(1);
  const [allTotal, setAllTotal] = useState(0);
  const [allType, setAllType] = useState<QuestionType | "">("");

  // selectedTicketId'ni ref'da ham saqlaymiz — loadTickets'ni har safar
  // qayta yaratmaslik uchun (aks holda bilet bosganda butun ro'yxat
  // qayta yuklanib, scroll boshiga qaytib ketardi)
  const selectedTicketIdRef = useRef<number | null>(null);
  useEffect(() => {
    selectedTicketIdRef.current = selectedTicketId;
  }, [selectedTicketId]);

  // ─── Load tickets list ──────────────────────────────────────────
  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const res = await adminAPI.getTicketsList();
      const list = res.data || [];
      setTickets(list);

      // Default: birinchi biletni tanlaymiz
      if (list.length > 0 && !selectedTicketIdRef.current) {
        setSelectedTicketId(list[0].id);
      }
    } catch {
      onError("Biletlar yuklanmadi");
    } finally {
      setTicketsLoading(false);
    }
  }, [onError]);

  const loadQuestions = useCallback(
    async (ticketId: number, opts?: { silent?: boolean }) => {
      // "silent" rejimda (masalan savol saqlangandan keyin) ro'yxatni
      // bo'shatib qo'ymaymiz — shu bois scroll pozitsiyasi buzilmaydi
      const scrollEl = questionsScrollRef.current;
      const prevScrollTop = scrollEl?.scrollTop ?? 0;

      if (!opts?.silent) {
        setQuestionsLoading(true);
        setQuestions([]);
      }
      try {
        const res = await adminAPI.getTicketQuestions(ticketId);
        setQuestions(res.data?.data || []);
        setTicketTotal(res.data?.total || 0);
        if (opts?.silent && scrollEl) {
          requestAnimationFrame(() => {
            scrollEl.scrollTop = prevScrollTop;
          });
        }
      } catch {
        onError("Savollar yuklanmadi");
      } finally {
        setQuestionsLoading(false);
      }
    },
    [onError],
  );

  // ─── Load ALL questions (bilet bilan cheklanmagan) ───────────────
  const loadAllQuestions = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setQuestionsLoading(true);
      try {
        const res = await adminAPI.getQuestions(
          allPage,
          ALL_QUESTIONS_LIMIT,
          allSearch,
          allType,
        );
        setQuestions(res.data?.data || []);
        setAllTotal(res.data?.meta?.total || 0);
      } catch {
        onError("Savollar yuklanmadi");
      } finally {
        setQuestionsLoading(false);
      }
    },
    [allPage, allSearch, allType, onError],
  );

  useEffect(() => {
    if (mode === "ticket") {
      loadTickets();
    }
  }, [mode, loadTickets]);

  useEffect(() => {
    if (mode === "ticket" && selectedTicketId !== null) {
      loadQuestions(selectedTicketId);
    }
  }, [mode, selectedTicketId, loadQuestions]);

  useEffect(() => {
    if (mode === "all") {
      loadAllQuestions();
    }
  }, [mode, loadAllQuestions]);

  const handleAllSearch = useCallback((value: string) => {
    setAllSearch(value);
    setAllPage(1);
  }, []);

  const handleAllTypeChange = useCallback((value: QuestionType | "") => {
    setAllType(value);
    setAllPage(1);
  }, []);

  // ─── Group tickets by bob ───────────────────────────────────────
  const ticketsByBob = tickets.reduce<Record<number, AdminTicketListItem[]>>(
    (acc, t) => {
      const bob = t.bob || 0;
      if (!acc[bob]) acc[bob] = [];
      acc[bob].push(t);
      return acc;
    },
    {},
  );

  const handleDeleteQuestion = async (questionId: number) => {
    if (!window.confirm("Rostdan ham ushbu savolni o'chirmoqchimisiz?")) return;
    try {
      await adminAPI.deleteQuestion(questionId);
      onSuccess("Savol muvaffaqiyatli o'chirildi");
      if (mode === "ticket") {
        loadTickets(); // Ticket countni yangilash uchun
        if (selectedTicketId)
          loadQuestions(selectedTicketId, { silent: true });
      } else {
        loadAllQuestions({ silent: true });
      }
    } catch (e: any) {
      onError(e?.response?.data?.message || "O'chirishda xatolik yuz berdi");
    }
  };

  const sortedBobs = Object.keys(ticketsByBob)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className="fade-up">
      {/* ═══════ MODE TOGGLE ═══════ */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setMode("ticket")}
          style={{
            padding: "9px 16px",
            borderRadius: 10,
            border: `1px solid ${mode === "ticket" ? "#3B82F6" : "#1E2D45"}`,
            background:
              mode === "ticket" ? "rgba(59,130,246,.12)" : "transparent",
            color: mode === "ticket" ? "#3B82F6" : "#64748B",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}>
          Bilet bo'yicha
        </button>
        <button
          onClick={() => setMode("all")}
          style={{
            padding: "9px 16px",
            borderRadius: 10,
            border: `1px solid ${mode === "all" ? "#3B82F6" : "#1E2D45"}`,
            background:
              mode === "all" ? "rgba(59,130,246,.12)" : "transparent",
            color: mode === "all" ? "#3B82F6" : "#64748B",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}>
          Barcha savollar
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: mode === "ticket" ? "280px 1fr" : "1fr",
          gap: 16,
          minHeight: "calc(100vh - 200px)",
        }}
        className="questions-grid">
        {/* ═══════ LEFT: TICKETS LIST (faqat "Bilet bo'yicha" rejimida) ═══════ */}
        {mode === "ticket" && (
        <div
          className="tickets-panel"
          style={{
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 16,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 200px)",
          }}>
          {/* Header */}
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid #1E2D45",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}>
            <FileText size={16} style={{ color: "#3B82F6" }} />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 14 }}>
              Biletlar
            </span>
            <span
              style={{
                marginLeft: "auto",
                color: "#475569",
                fontSize: 12,
                fontWeight: 600,
              }}>
              {tickets.length}
            </span>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
            {ticketsLoading ?
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  color: "#475569",
                  fontSize: 13,
                }}>
                Yuklanmoqda...
              </div>
            : tickets.length === 0 ?
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  color: "#475569",
                  fontSize: 13,
                }}>
                Biletlar topilmadi
              </div>
            : sortedBobs.map((bob) => (
                <div key={bob} style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      padding: "8px 12px 6px",
                      color: "#475569",
                      fontSize: 10,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}>
                    {bob === 0 ? "Boshqa biletlar" : `Bob ${bob}`}{" "}
                  </div>

                  {ticketsByBob[bob].map((ticket) => {
                    const active = selectedTicketId === ticket.id;
                    return (
                      <div
                        key={ticket.id}
                        onClick={() => setSelectedTicketId(ticket.id)}
                        className="nav-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          borderRadius: 10,
                          marginBottom: 3,
                          cursor: "pointer",
                          background:
                            active ? "rgba(59,130,246,.15)" : "transparent",
                          border:
                            active ?
                              "1px solid rgba(59,130,246,.3)"
                            : "1px solid transparent",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}>
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 8,
                              background: active ? "#3B82F6" : "#1E2D45",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 800,
                              color: active ? "#fff" : "#64748B",
                            }}>
                            {ticket.id}
                          </div>
                          <span
                            style={{
                              color: active ? "#fff" : "#94A3B8",
                              fontWeight: active ? 700 : 500,
                              fontSize: 13,
                            }}>
                            Bilet {ticket.id}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: active ? "#3B82F6" : "#475569",
                            background:
                              active ?
                                "rgba(59,130,246,.1)"
                              : "rgba(100,116,139,.1)",
                            padding: "2px 8px",
                            borderRadius: 99,
                          }}>
                          {ticket.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))
            }
          </div>
        </div>
        )}

        {/* ═══════ RIGHT: QUESTIONS ═══════ */}
        <div
          className="questions-panel"
          style={{
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 16,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 200px)",
          }}>
          {/* Header */}
          <div
            style={{
              padding: "14px 20px",
              borderBottom: "1px solid #1E2D45",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BookOpen size={16} style={{ color: "#22C55E" }} />
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 14 }}>
                {mode === "all" ?
                  "Barcha savollar"
                : selectedTicketId ?
                  `Bilet ${selectedTicketId} savollari`
                : "Savollarni ko'rish uchun bilet tanlang"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {mode === "ticket" && selectedTicketId && (
                <span
                  style={{ color: "#475569", fontSize: 12, fontWeight: 600 }}>
                  {questions.length} / {ticketTotal} ta savol
                </span>
              )}
              {mode === "all" && (
                <span
                  style={{ color: "#475569", fontSize: 12, fontWeight: 600 }}>
                  {allTotal} ta savol
                </span>
              )}
              <button
                onClick={() => setCreateModalOpen(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: "#3B82F6",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                }}>
                <Plus size={15} /> Yangi savol
              </button>
            </div>
          </div>

          {/* Search + tur filtri (faqat "Barcha savollar" rejimida) */}
          {mode === "all" && (
            <div
              style={{
                padding: "12px 16px 0",
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <SearchBar
                  value={allSearch}
                  onChange={handleAllSearch}
                  placeholder="Savol matni bo'yicha qidirish..."
                />
              </div>
              <select
                value={allType}
                onChange={(e) =>
                  handleAllTypeChange(e.target.value as QuestionType | "")
                }
                style={{
                  padding: "10px 14px",
                  borderRadius: 12,
                  border: "1px solid #1E2D45",
                  background: "#111827",
                  color: "#E2E8F0",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}>
                <option value="">Barcha turlar</option>
                <option value={QuestionType.ODDIY}>Oddiy</option>
                <option value={QuestionType.SONLI}>Sonli</option>
                <option value={QuestionType.KALIT_SOZLI}>Kalit so'zli</option>
                <option value={QuestionType.MURAKKAB}>Murakkab</option>
              </select>
            </div>
          )}

          {/* Questions list */}
          <div
            ref={questionsScrollRef}
            style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {questionsLoading ?
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "#475569",
                  fontSize: 13,
                }}>
                Yuklanmoqda...
              </div>
            : mode === "ticket" && !selectedTicketId ?
              <div
                style={{
                  padding: 60,
                  textAlign: "center",
                  color: "#475569",
                  fontSize: 14,
                }}>
                Chap tomondan bilet tanlang
              </div>
            : questions.length === 0 ?
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "#475569",
                  fontSize: 13,
                }}>
                {mode === "all" ? "Savollar topilmadi" : "Bu biletda savollar yo'q"}
              </div>
            : <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {questions.map((q, idx) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    number={idx + 1}
                    onEdit={() => {
                      setEditQuestionId(q.id);
                      setEditModalOpen(true);
                    }}
                    onDelete={() => handleDeleteQuestion(q.id)} // To'g'ri bog'landi
                  />
                ))}
              </div>
            }
          </div>
          {mode === "all" && (
            <div style={{ padding: "0 16px 12px" }}>
              <Pagination
                page={allPage}
                total={allTotal}
                limit={ALL_QUESTIONS_LIMIT}
                onChange={setAllPage}
              />
            </div>
          )}
        </div>
      </div>

      <EditQuestionModal
        questionId={editQuestionId}
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditQuestionId(null);
        }}
        onSaved={() => {
          if (mode === "ticket" && selectedTicketId) {
            loadQuestions(selectedTicketId, { silent: true });
          } else if (mode === "all") {
            loadAllQuestions({ silent: true });
          }
        }}
        onError={onError}
        onSuccess={onSuccess}
      />
      <CreateQuestionModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          if (mode === "ticket") {
            loadTickets();
            if (selectedTicketId) loadQuestions(selectedTicketId);
          } else {
            loadAllQuestions();
          }
        }}
        onError={onError}
        onSuccess={onSuccess}
      />
      <style>{`
        @media (max-width: 900px) {
          .questions-grid {
            grid-template-columns: 1fr !important;
            min-height: 0 !important;
          }
          .tickets-panel {
            max-height: 260px !important;
          }
          .questions-panel {
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// QUESTION CARD — alohida component
// ═══════════════════════════════════════════════════════════════════

interface QuestionCardProps {
  question: AdminQuestionResponse;
  number: number;
  onEdit: () => void;
  onDelete: () => void; // Interface joyiga tushdi
}

function QuestionCard({
  question,
  number,
  onEdit,
  onDelete,
}: QuestionCardProps) {
  const photoUrl =
    question.photo_url ? `${IMG_BASE}/${question.photo_url}` : null;
  const [zoomOpen, setZoomOpen] = useState(false);

  return (
    <>
      <div
        style={{
          background: "#0D1526",
          border: "1px solid #1E2D45",
          borderRadius: 14,
          padding: 16,
          position: "relative",
        }}>
        {/* Header: number + action buttons (Edit, Delete) */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "rgba(59,130,246,.15)",
                border: "1px solid rgba(59,130,246,.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 12,
                color: "#3B82F6",
                flexShrink: 0,
              }}>
              {number}
            </div>
            <span style={{ color: "#475569", fontSize: 11, fontWeight: 600 }}>
              #{question.id}
            </span>
          </div>

          {/* Tugmalar bitta chiroyli blokda jamlandi */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
            }}>
            {!!question.mistake_count && (
              <span
                title="Bu savolga jami xato javoblar soni"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "5px 10px",
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  background: "rgba(239,68,68,.08)",
                  border: "1px solid rgba(239,68,68,.25)",
                  color: "#F87171",
                  marginRight: 2,
                }}>
                ❌ {question.mistake_count}
              </span>
            )}
            <button
              onClick={onEdit}
              title="Tahrirlash"
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: "1px solid rgba(59,130,246,.3)",
                background: "rgba(59,130,246,.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}>
              <Edit2 size={12} color="#3B82F6" />
            </button>
            <button
              onClick={onDelete}
              title="O'chirish"
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: "1px solid rgba(239,68,68,.3)",
                background: "rgba(239,68,68,.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}>
              <Trash2 size={12} color="#EF4444" />
            </button>
          </div>
        </div>

        {/* Body: image + question + choices */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: photoUrl ? "140px 1fr" : "1fr",
            gap: 14,
            alignItems: "start",
          }}>
          {photoUrl && (
            <button
              onClick={() => setZoomOpen(true)}
              title="Kattalashtirish"
              style={{
                width: 140,
                height: 100,
                borderRadius: 10,
                overflow: "hidden",
                background: "#000",
                border: "1px solid #1E2D45",
                padding: 0,
                cursor: "zoom-in",
                position: "relative",
                transition: "border-color 0.15s, transform 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#3B82F6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#1E2D45";
              }}>
              <img
                src={photoUrl}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
                onError={(e) => {
                  (e.currentTarget.parentElement as HTMLElement).style.display =
                    "none";
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: "rgba(0,0,0,.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  color: "#fff",
                }}>
                ⛶
              </div>
            </button>
          )}

          {/* Content */}
          <div>
            <p
              style={{
                color: "#E2E8F0",
                fontSize: 13,
                lineHeight: 1.5,
                margin: 0,
                marginBottom: 10,
                fontWeight: 500,
              }}>
              {question.question_oz}
            </p>

            {/* Savol turi (doim ko'rinadi) + kalit so'zlar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 10,
              }}>
              {(() => {
                const t = question.question_type || QuestionType.ODDIY;
                const c = QUESTION_TYPE_COLORS[t];
                return (
                  <span
                    style={{
                      padding: "3px 8px",
                      borderRadius: 6,
                      background: c.bg,
                      border: `1px solid ${c.border}`,
                      color: c.text,
                      fontSize: 10,
                      fontWeight: 700,
                    }}>
                    {QUESTION_TYPE_LABELS[t]}
                  </span>
                );
              })()}
                {question.keywords?.map((kw) => (
                  <span
                    key={kw}
                    style={{
                      padding: "3px 8px",
                      borderRadius: 6,
                      background: "rgba(59,130,246,.1)",
                      border: "1px solid rgba(59,130,246,.2)",
                      color: "#60A5FA",
                      fontSize: 10,
                      fontWeight: 600,
                    }}>
                    {kw}
                  </span>
                ))}
            </div>

            {/* Choices */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {question.choices
                .sort((a, b) => a.order_index - b.order_index)
                .map((choice) => {
                  const isCorrect =
                    choice.order_index === question.correct_answer_index;
                  return (
                    <div
                      key={choice.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 10px",
                        borderRadius: 8,
                        background:
                          isCorrect ? "rgba(34,197,94,.08)" : "transparent",
                        border:
                          isCorrect ?
                            "1px solid rgba(34,197,94,.2)"
                          : "1px solid transparent",
                      }}>
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: isCorrect ? "#22C55E" : "#1E2D45",
                          color: isCorrect ? "#fff" : "#64748B",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}>
                        {choice.order_index}
                      </span>
                      <span
                        style={{
                          color: isCorrect ? "#86EFAC" : "#94A3B8",
                          fontSize: 12,
                          flex: 1,
                        }}>
                        {choice.text_oz}
                      </span>
                      {isCorrect && (
                        <CheckCircle2
                          size={14}
                          style={{ color: "#22C55E", flexShrink: 0 }}
                        />
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {zoomOpen && photoUrl && (
        <ImageZoomModal src={photoUrl} onClose={() => setZoomOpen(false)} />
      )}
    </>
  );
}
